import React from 'react';
import {motion,useScroll,useTransform} from 'framer-motion';
import {useNavigate} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiArrowRight,FiCheck,FiShield,FiLock,FiStar,FiUserCheck,
  FiPackage,FiCheckCircle,FiAward,FiActivity,FiScissors,FiTrendingUp
} = FiIcons;

const stats = [
  {value: '50,000+',label: 'Patients Served'},
  {value: '4.9★',label: 'Average Rating'},
  {value: '48hr',label: 'Avg. Delivery'},
  {value: '100%',label: 'Licensed Clinicians'},
];

const trustItems = [
  {icon: FiAward,title: 'LegitScript Certified',text: 'Verified safe and legal healthcare provider'},
  {icon: FiShield,title: 'HIPAA Compliant',text: 'Bank-level encryption for your health data'},
  {icon: FiCheckCircle,title: 'FDA Registered',text: 'Medications from licensed 503B pharmacies'},
  {icon: FiUserCheck,title: 'Board-Certified',text: 'Every case reviewed by a licensed clinician'},
];

const carePaths = [
  {
    title: 'Weight Loss',
    subtitle: 'GLP-1 Medications',
    description: 'Physician-guided semaglutide and tirzepatide programs tailored to your body and goals.',
    icon: FiActivity,
    path: '/weightquiz',
    gradient: 'from-emerald-600 to-teal-500',
    bg: 'bg-emerald-50',
    accent: 'text-emerald-700',
    border: 'border-emerald-100',
    image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=800&q=85',
  },
  {
    title: "Men's Health",
    subtitle: 'ED & Wellness',
    description: 'Private, convenient care for erectile dysfunction and men\'s wellness from home.',
    icon: FiShield,
    path: '/menquiz',
    gradient: 'from-sky-600 to-blue-500',
    bg: 'bg-sky-50',
    accent: 'text-sky-700',
    border: 'border-sky-100',
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=800&q=85',
  },
  {
    title: 'Hair Loss',
    subtitle: 'Regrowth Programs',
    description: 'Clinician-selected finasteride, dutasteride and minoxidil regimens for lasting results.',
    icon: FiScissors,
    path: '/hairquiz',
    gradient: 'from-violet-600 to-fuchsia-500',
    bg: 'bg-violet-50',
    accent: 'text-violet-700',
    border: 'border-violet-100',
    image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?auto=format&fit=crop&w=800&q=85',
  },
];

const steps = [
  {num: '01', title: 'Complete Your Intake', text: 'Answer a short, secure questionnaire about your health history and goals — takes under 5 minutes.', icon: FiUserCheck},
  {num: '02', title: 'Clinician Review', text: 'A licensed medical provider independently reviews your information and determines what is right for you.', icon: FiCheckCircle},
  {num: '03', title: 'Receive Your Care', text: 'When prescribed, your medication ships from a licensed US pharmacy in discreet packaging to your door.', icon: FiPackage},
];

const testimonials = [
  {name: 'Sarah J.', location: 'Austin, TX', rating: 5, text: '"Down 28 lbs in 4 months. The process was seamless and my doctor was incredibly responsive. Best decision I\'ve made for my health."', detail: 'Weight Loss · Semaglutide'},
  {name: 'Marcus T.', location: 'Seattle, WA', rating: 5, text: '"I was skeptical at first, but the care felt genuinely personal. My provider took time to explain everything clearly."', detail: "Men's Health"},
  {name: 'Bryon W.', location: 'Miami, FL', rating: 5, text: '"Comparing photos at month 6 was incredible. The consistency of the plan made all the difference for my hairline."', detail: 'Hair Loss · Month 6'},
];

function StatBar() {
  return (
    <div className="relative z-10 mx-auto max-w-5xl px-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {stats.map((stat) => (
          <motion.div key={stat.label}
            initial={{opacity: 0, y: 16}} whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}} transition={{duration: 0.5}}
            className="rounded-2xl border border-white/60 bg-white/80 backdrop-blur-sm p-5 text-center shadow-lg shadow-teal-950/5">
            <p className="text-2xl font-extrabold text-slate-900 sm:text-3xl">{stat.value}</p>
            <p className="mt-1 text-xs font-bold uppercase tracking-widest text-slate-500">{stat.label}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function HeroSection({navigate}) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#f0faf8] via-[#f7fbfa] to-[#eef6ff] pb-24 pt-32 lg:pt-44 lg:pb-32">
      <div className="absolute -left-40 -top-20 h-[600px] w-[600px] rounded-full bg-teal-200/40 blur-3xl" />
      <div className="absolute -right-40 top-40 h-[500px] w-[500px] rounded-full bg-sky-200/40 blur-3xl" />
      <div className="absolute bottom-0 left-1/3 h-[300px] w-[300px] rounded-full bg-emerald-100/60 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-16 lg:grid-cols-[1.1fr_0.9fr]">
          <motion.div initial={{opacity: 0, y: 28}} animate={{opacity: 1, y: 0}} transition={{duration: 0.7}}>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white px-4 py-2 text-xs font-extrabold uppercase tracking-[0.18em] text-teal-800 shadow-sm">
              <SafeIcon icon={FiShield} className="text-teal-600" />
              Clinician-Guided Telehealth
            </div>
            <h1 className="text-5xl font-extrabold leading-[1.03] tracking-tight text-slate-950 sm:text-6xl lg:text-7xl">
              Healthcare that
              <span className="block mt-1 bg-gradient-to-r from-teal-600 to-emerald-500 bg-clip-text text-transparent">
                comes to you.
              </span>
            </h1>
            <p className="mt-7 max-w-xl text-xl leading-relaxed text-slate-600">
              Explore clinician-reviewed care for weight loss, men's health, and hair loss — all from home. Every treatment is independently evaluated by a licensed physician.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <button onClick={() => navigate('/mytreatments')}
                className="group inline-flex items-center justify-center gap-3 rounded-full bg-slate-950 px-8 py-4 text-lg font-extrabold text-white shadow-xl shadow-slate-900/20 transition hover:bg-teal-700 hover:shadow-teal-700/25">
                Start Your Evaluation
                <SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-1" />
              </button>
              <button onClick={() => navigate('/mytreatments')}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-8 py-4 text-lg font-bold text-slate-800 shadow-sm transition hover:border-teal-300 hover:bg-teal-50">
                View Care Options
              </button>
            </div>
            <div className="mt-10 grid max-w-lg grid-cols-2 gap-3">
              {['Licensed clinician review','Private online intake','Licensed pharmacy fulfillment','Discreet home delivery'].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700">
                    <SafeIcon icon={FiCheck} className="h-3 w-3" />
                  </span>
                  {item}
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{opacity: 0, scale: 0.95}} animate={{opacity: 1, scale: 1}} transition={{duration: 0.7, delay: 0.15}} className="relative hidden lg:block">
            <div className="absolute -inset-4 rounded-[3rem] bg-gradient-to-br from-teal-200/50 to-sky-200/50 blur-2xl" />
            <div className="relative overflow-hidden rounded-[2.5rem] shadow-2xl shadow-slate-900/20">
              <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=1200&q=90"
                alt="Healthcare professional"
                className="h-[540px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-md text-white">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-400/20 text-teal-200">
                    <SafeIcon icon={FiUserCheck} className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-extrabold uppercase tracking-widest text-teal-200">Care with confidence</p>
                    <p className="mt-0.5 font-bold">Every case reviewed by a licensed physician.</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-sm text-slate-200">
                  <SafeIcon icon={FiLock} className="text-teal-300" />
                  Your intake is secure, private, and HIPAA-compliant.
                </div>
              </div>
            </div>
            <div className="absolute -right-6 top-12 rounded-2xl border border-white/80 bg-white p-4 shadow-xl">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-500">
                  <SafeIcon icon={FiStar} className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-500">Patient Rating</p>
                  <p className="text-xl font-extrabold text-slate-900">4.9 / 5.0</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="mt-20">
        <StatBar />
      </div>
    </section>
  );
}

function TrustBar() {
  return (
    <section className="border-y border-slate-900/5 bg-slate-950 py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <p className="mb-10 text-center text-xs font-extrabold uppercase tracking-[0.25em] text-teal-400">
          Verified Standards of Care
        </p>
        <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
          {trustItems.map((item, i) => (
            <motion.div key={item.title}
              initial={{opacity: 0, y: 16}} whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}} transition={{delay: i * 0.08}}
              className="flex flex-col items-center gap-3 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-400/10 text-teal-400">
                <SafeIcon icon={item.icon} className="h-6 w-6" />
              </div>
              <div>
                <p className="font-extrabold text-white">{item.title}</p>
                <p className="mt-1 text-sm text-slate-400">{item.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CarePathsSection({navigate}) {
  return (
    <section className="bg-white py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-extrabold uppercase tracking-[0.2em] text-teal-700">Choose your starting point</p>
          <h2 className="mt-4 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            Care designed around what matters to you.
          </h2>
          <p className="mt-5 text-xl leading-relaxed text-slate-600">
            Begin with a short, private assessment. A licensed clinician reviews your information and determines whether treatment may be appropriate.
          </p>
        </div>
        <div className="mt-14 grid gap-7 md:grid-cols-3">
          {carePaths.map((path, i) => (
            <motion.div key={path.title}
              initial={{opacity: 0, y: 24}} whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}} transition={{delay: i * 0.1}}
              onClick={() => navigate(path.path)}
              className="group cursor-pointer overflow-hidden rounded-[2rem] border border-slate-100 bg-white shadow-lg transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
              <div className="relative h-56 overflow-hidden">
                <img src={path.image} alt={path.title}
                  className="h-full w-full object-cover transition duration-700 group-hover:scale-110" />
                <div className={`absolute inset-0 bg-gradient-to-t ${path.gradient} opacity-75`} />
                <div className="absolute bottom-5 left-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/30 bg-white/15 backdrop-blur-sm text-white">
                    <SafeIcon icon={path.icon} className="h-6 w-6" />
                  </div>
                </div>
                <div className="absolute right-4 top-4 rounded-full border border-white/30 bg-white/15 px-3 py-1 text-xs font-bold text-white backdrop-blur-sm">
                  {path.subtitle}
                </div>
              </div>
              <div className="p-7">
                <h3 className="text-2xl font-extrabold tracking-tight text-slate-900">{path.title}</h3>
                <p className="mt-3 leading-relaxed text-slate-600">{path.description}</p>
                <div className={`mt-6 inline-flex items-center gap-2 font-extrabold ${path.accent}`}>
                  Start private assessment
                  <SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  return (
    <section className="relative overflow-hidden bg-slate-950 py-28 text-white">
      <div className="absolute -left-32 top-0 h-96 w-96 rounded-full bg-teal-900/30 blur-3xl" />
      <div className="absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-sky-900/20 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-extrabold uppercase tracking-[0.2em] text-teal-400">Simple from start to finish</p>
          <h2 className="mt-4 text-4xl font-extrabold tracking-tight sm:text-5xl">A thoughtful path to care.</h2>
          <p className="mt-5 text-lg leading-relaxed text-slate-300">
            From your first intake to ongoing support, RenewRX makes it easy to explore care with privacy, clarity, and clinical guidance.
          </p>
        </div>
        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {steps.map((step, i) => (
            <motion.div key={step.num}
              initial={{opacity: 0, y: 20}} whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}} transition={{delay: i * 0.1}}
              className="relative rounded-[2rem] border border-slate-700 bg-slate-900 p-8">
              <span className="text-6xl font-extrabold text-teal-400/25">{step.num}</span>
              <div className="mt-5 flex h-12 w-12 items-center justify-center rounded-xl bg-teal-400/10 text-teal-300">
                <SafeIcon icon={step.icon} className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-2xl font-extrabold">{step.title}</h3>
              <p className="mt-4 leading-relaxed text-slate-400">{step.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AuthoritySection() {
  return (
    <section className="relative overflow-hidden bg-[#f0faf8] py-28">
      <div className="absolute -left-40 top-10 h-96 w-96 rounded-full bg-teal-200/50 blur-3xl" />
      <div className="absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-sky-200/50 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-16 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-sm font-extrabold uppercase tracking-[0.2em] text-teal-700">A higher standard of online care</p>
            <h2 className="mt-4 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              Clinical guidance you can feel confident about.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-slate-600">
              RenewRX makes it easier to take the next step. Your health information is reviewed by a licensed clinician who determines whether treatment is medically appropriate for you.
            </p>
            <div className="mt-8 border-l-4 border-teal-500 pl-5">
              <p className="text-sm font-semibold leading-relaxed text-slate-600">
                Prescription eligibility, medication selection, and dosage are determined solely through an independent clinical review.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-2 gap-4">
              {[
                {icon: FiUserCheck, label: 'Licensed clinician review'},
                {icon: FiLock, label: 'Private by design'},
                {icon: FiPackage, label: 'Pharmacy fulfillment'},
                {icon: FiCheckCircle, label: 'Ongoing care support'},
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3 rounded-2xl border border-white bg-white p-4 shadow-sm">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                    <SafeIcon icon={item.icon} className="h-5 w-5" />
                  </div>
                  <span className="text-sm font-bold text-slate-800">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 rounded-[3rem] bg-teal-200/40 blur-2xl" />
            <div className="relative overflow-hidden rounded-[2.5rem] shadow-2xl shadow-teal-950/10">
              <img src="https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=900&q=85"
                alt="Healthcare professional" className="h-[500px] w-full object-cover" />
              <div className="absolute bottom-6 left-6 right-6 rounded-2xl border border-white/30 bg-slate-950/75 px-5 py-4 text-white backdrop-blur-md">
                <p className="text-xs font-extrabold uppercase tracking-wider text-teal-200">Your care</p>
                <p className="mt-1 font-bold">Reviewed thoughtfully, every step of the way.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section className="bg-white py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-extrabold uppercase tracking-[0.2em] text-teal-700">Patient Success Stories</p>
          <h2 className="mt-4 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">Real people. Real results.</h2>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {testimonials.map((t, i) => (
            <motion.div key={t.name}
              initial={{opacity: 0, y: 24}} whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}} transition={{delay: i * 0.1}}
              className="flex flex-col rounded-[2rem] border border-slate-100 bg-white p-8 shadow-lg shadow-slate-900/5">
              <div className="flex gap-1 text-amber-400">
                {[...Array(t.rating)].map((_, j) => (
                  <SafeIcon key={j} icon={FiStar} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <blockquote className="mt-5 flex-grow text-lg leading-relaxed text-slate-700">{t.text}</blockquote>
              <div className="mt-8 border-t border-slate-100 pt-5">
                <p className="font-extrabold text-slate-900">{t.name}</p>
                <p className="mt-1 text-sm text-slate-500">{t.location} · {t.detail}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection({navigate}) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-teal-700 to-emerald-600 py-28 text-white">
      <div className="absolute -left-20 top-0 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
      <div className="absolute -right-20 bottom-0 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
      <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
          <SafeIcon icon={FiTrendingUp} className="h-8 w-8" />
        </div>
        <h2 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
          Ready to explore care that fits your life?
        </h2>
        <p className="mx-auto mt-6 max-w-2xl text-xl leading-relaxed text-teal-50">
          Choose a care path, answer a few questions privately, and see what a clinician may be able to help with.
        </p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <button onClick={() => navigate('/mytreatments')}
            className="group inline-flex items-center gap-3 rounded-full bg-white px-9 py-4 text-lg font-extrabold text-teal-800 shadow-xl transition hover:bg-teal-50 hover:shadow-2xl">
            Explore Your Care Options
            <SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
        <p className="mt-7 text-sm text-teal-100">
          A licensed clinician determines eligibility and whether treatment is medically appropriate.
        </p>
      </div>
    </section>
  );
}

export default function MainHome() {
  const navigate = useNavigate();
  return (
    <div className="overflow-hidden">
      <HeroSection navigate={navigate} />
      <TrustBar />
      <CarePathsSection navigate={navigate} />
      <HowItWorksSection />
      <AuthoritySection />
      <TestimonialsSection />
      <CtaSection navigate={navigate} />
    </div>
  );
}